'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';

import { Link as IntlLink } from '@/i18n/navigation';

import { useAuthModal } from './AuthModalProvider';
import { SceneCanvas } from './SceneCanvas';

interface IndustryMeta {
	id: string;
	tag: string;
	scene: string;
	client: string;
	/** Dedicated industry page, when one exists — enables the "Learn more" link. */
	href?: string;
}

// Structural data (HUD codes / scene ids / client refs stay literal); copy i18n.
const INDUSTRIES: readonly IndustryMeta[] = [
	{
		id: 'fintech',
		tag: 'FIN',
		scene: 'kyc-02',
		client: 'NOAH',
		href: '/industries/fintech',
	},
	{
		id: 'crypto',
		tag: 'CHAIN',
		scene: 'crypto',
		client: 'LBANK',
		href: '/industries/crypto',
	},
	{
		id: 'igaming',
		tag: 'GAME',
		scene: 'device',
		client: 'KAIZEN',
		href: '/industries/igaming',
	},
	{
		id: 'trading',
		tag: 'TRADE',
		scene: 'kyc-05',
		client: 'TAUREX',
		href: '/industries/trading',
	},
	{
		id: 'mobility',
		tag: 'MOVE',
		scene: 'device',
		client: 'COOLTRA',
		href: '/industries/mobility',
	},
	{
		id: 'markets',
		tag: 'MARKET',
		scene: 'biz-tree',
		client: 'ZUBALE',
		href: '/industries/marketplaces',
	},
	{
		id: 'banking',
		tag: 'BANK',
		scene: 'card',
		client: 'NEON',
		href: '/industries/neobank',
	},
	{
		id: 'lending',
		tag: 'LEND',
		scene: 'kyc-04',
		client: 'KREDIT',
		href: '/industries/lending',
	},
];

/**
 * Solutions — pick an industry to reveal a tailored detail panel, plus a
 * results (TEI/ROI) band. Like the Products deck, the dark surfaces use the
 * fg/bg tokens so they invert with the theme (dark on light, light on dark),
 * the scenes invert (.products-scene) and the veil flips via --card-veil.
 */
export function SolutionsSection() {
	const t = useTranslations('Solutions');
	const tCta = useTranslations('Cta');
	const { open: openAuth } = useAuthModal();
	const [sel, setSel] = useState(1);
	const d = INDUSTRIES[sel];
	const detName = t(`ind.${d.id}.name`);
	const detSub = t.raw(`ind.${d.id}.sub`) as [string, string][];
	const roi = t.raw('roi') as [string, string][];

	return (
		<section
			id="solutions"
			className="relative scroll-mt-36 py-16 sm:scroll-mt-44 sm:py-24"
		>
			{/* Section header */}
			<div className="mb-5 sm:mb-6">
				<div className="label-mono">SECTION_04 // SOLUTIONS</div>
				<div className="mt-2 flex flex-wrap items-end justify-between gap-x-5 gap-y-2.5">
					<h2 className="text-[clamp(30px,4.6vw,52px)] leading-none font-normal tracking-[-0.03em]">
						{t('heading1')}
						<br />
						{t('heading2')}
					</h2>
					<p className="text-accent max-w-[38ch] pb-1 text-[14px] leading-[1.6]">
						{t('intro')}
					</p>
				</div>
				<div className="bg-border mt-[22px] h-px" />
			</div>

			{/* Industry tiles */}
			<div className="mt-6 grid grid-cols-[repeat(auto-fit,minmax(270px,1fr))] gap-[14px]">
				{INDUSTRIES.map((ind, i) => {
					const active = i === sel;
					return (
						<button
							key={ind.id}
							type="button"
							onClick={() => setSel(i)}
							aria-pressed={active}
							className={`relative flex min-h-[188px] cursor-crosshair flex-col overflow-hidden border p-[18px_20px_20px] text-left transition-colors ${
								active
									? 'border-fg bg-fg text-bg'
									: 'border-border bg-surface text-fg hover:border-fg'
							}`}
						>
							{active && (
								<SceneCanvas
									scene={ind.scene}
									className="products-scene absolute inset-0 h-full w-full opacity-[0.28]"
								/>
							)}
							<div className="relative z-[1] flex h-full flex-col">
								<div
									className={`flex items-center justify-between font-mono text-[10px] tracking-[1.5px] ${active ? 'text-bg/72' : 'text-muted'}`}
								>
									<span>[ {ind.tag} ]</span>
									<span
										className={`flex h-6 w-6 items-center justify-center border ${active ? 'border-bg' : 'border-border'}`}
										aria-hidden="true"
									>
										→
									</span>
								</div>
								<div className="mt-auto font-mono text-[42px] leading-none font-bold tracking-[-0.02em]">
									{t(`ind.${ind.id}.stat`)}
								</div>
								<div
									className={`mt-2 text-[12.5px] leading-[1.4] ${active ? 'text-bg/70' : 'text-muted'}`}
								>
									{t(`ind.${ind.id}.metric`)}
								</div>
								<div className="mt-3.5 text-[18px] font-medium tracking-[-0.015em]">
									{t(`ind.${ind.id}.name`)}
								</div>
							</div>
						</button>
					);
				})}
			</div>

			{/* Selected-industry detail panel */}
			<div className="border-fg bg-fg mt-3.5 grid grid-cols-[repeat(auto-fit,minmax(min(100%,300px),1fr))] gap-[2px] border">
				<div className="bg-fg text-bg relative min-h-[230px] overflow-hidden p-[26px]">
					<SceneCanvas
						scene={d.scene}
						className="products-scene absolute inset-0 h-full w-full opacity-40"
					/>
					<div className="products-veil-collapsed pointer-events-none absolute inset-0" />
					<div className="relative z-[1] flex h-full flex-col">
						<div className="text-bg/72 flex justify-between font-mono text-[10.5px] tracking-[1.5px]">
							<span>SOLUTION // {d.tag}</span>
							<span>CLIENT: {d.client}</span>
						</div>
						<div className="mt-auto font-mono text-[clamp(40px,6vw,60px)] leading-none font-bold tracking-[-0.02em]">
							{t(`ind.${d.id}.stat`)}
						</div>
						<div className="text-bg/70 mt-2 text-[13.5px]">
							{t(`ind.${d.id}.metric`)}
						</div>
					</div>
				</div>
				<div className="bg-fg text-bg flex flex-col p-[26px]">
					<div className="text-[22px] font-medium tracking-[-0.015em]">
						{detName}
					</div>
					<p className="text-bg/70 mt-3 text-[14px] leading-[1.6]">
						{t(`ind.${d.id}.desc`)}
					</p>
					<div className="mt-5 flex gap-2.5">
						{detSub.map(([value, label]) => (
							<div key={label} className="border-bg/15 flex-1 border p-3.5">
								<div className="font-mono text-[20px] font-bold">{value}</div>
								<div className="text-bg/72 mt-1.5 font-mono text-[9.5px] tracking-[1px]">
									{label}
								</div>
							</div>
						))}
					</div>
					<div className="mt-auto flex flex-col gap-2.5">
						{d.href && (
							<IntlLink
								href={d.href}
								className="border-bg/30 text-bg hover:border-bg flex cursor-crosshair items-center justify-between gap-3 border px-[18px] py-[15px] font-mono text-[11.5px] tracking-[1.5px] transition-colors"
							>
								<span>{t('learnMore')}</span>
								<span aria-hidden="true">-&gt;</span>
							</IntlLink>
						)}
						<button
							type="button"
							onClick={() => openAuth('register')}
							className="bg-bg text-fg flex cursor-crosshair items-center justify-between gap-3 px-[18px] py-[15px] font-mono text-[11.5px] tracking-[1.5px] transition-opacity hover:opacity-85"
						>
							<span>{t('launchCta', { name: detName })}</span>
							<span aria-hidden="true">-&gt;</span>
						</button>
					</div>
				</div>
			</div>

			{/* Results / ROI band */}
			<div className="bg-fg text-bg relative mt-[54px] overflow-hidden p-[clamp(28px,4vw,48px)]">
				<div className="bg-bg/15 absolute inset-x-0 top-0 h-px overflow-hidden">
					<div className="solutions-sweep absolute top-0 left-0 h-px w-[36%] bg-[linear-gradient(90deg,transparent,var(--color-bg),transparent)]" />
				</div>
				<div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,300px),1fr))] items-start gap-x-10 gap-y-6">
					<div>
						<div className="text-bg/72 font-mono text-[11px] tracking-[2px]">
							RESULTS // TEI
						</div>
						<h3 className="mt-3 text-[clamp(28px,3.6vw,42px)] leading-[1.04] font-normal tracking-[-0.02em]">
							{t('resultsHeading1')}
							<br />
							{t('resultsHeading2')}
						</h3>
					</div>
					<div className="flex flex-col gap-[18px]">
						<p className="text-bg/70 max-w-[52ch] text-[14px] leading-[1.6]">
							{t('resultsIntro')}
						</p>
						<button
							type="button"
							onClick={() => openAuth('register')}
							className="bg-bg text-fg inline-flex cursor-crosshair items-center gap-3 self-start px-[22px] py-[15px] font-mono text-[12px] tracking-[1.5px] uppercase transition-opacity hover:opacity-85"
						>
							{tCta('getStarted')}
							<span aria-hidden="true">-&gt;</span>
						</button>
					</div>
				</div>
				<div className="mt-9 grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3.5">
					{roi.map(([value, label]) => (
						<div key={label} className="border-bg/15 border p-[22px_20px]">
							<div className="font-mono text-[clamp(28px,3.4vw,40px)] leading-none font-bold tracking-[-0.02em]">
								{value}
							</div>
							<div className="text-bg/72 mt-3 font-mono text-[11px] tracking-[1px]">
								{label}
							</div>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}
